import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GraduationCapIcon, PlusIcon, TrashIcon, UploadSimpleIcon } from '@phosphor-icons/react';

import {
  createAdminSkill,
  deleteAdminSkill,
  importAdminSkillZip,
  isValidSkillName,
  seedSkillMd,
  useAdminSkills,
} from '@/api-models/skills';
import { errorText } from '@/features/admin/format';
import { Button, IconButton } from '@/ui/button/Button';
import { Card } from '@/ui/Card';
import { Input } from '@/ui/Input';
import { Modal } from '@/ui/Modal';
import { Spinner } from '@/ui/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';
import { Textarea } from '@/ui/Textarea';
import { formatTimeAgo } from '@/utils/format';

/**
 * Admin → Skills: the system tier.
 *
 * Published to every space read-only, so an edit here reaches every team's agents on their next
 * session. Teams can switch one off for themselves or fork it, but not change it.
 */
export function AdminSkillsPage() {
  const { skills, mutate, isLoading } = useAdminSkills();
  const [creating, setCreating] = useState(false);
  const importInput = useRef<HTMLInputElement>(null);

  async function handleImport(file: File) {
    try {
      const created = await importAdminSkillZip(file);
      await mutate();
      toast.success(`Imported "${created.name}"`);
    } catch (e) {
      toast.error(errorText(e, 'Could not import that zip'));
    }
  }

  async function handleDelete(id: string, name: string) {
    try {
      await deleteAdminSkill(id);
      await mutate();
      toast.success(`Deleted "${name}"`);
    } catch (e) {
      toast.error(errorText(e, 'Could not delete this skill'));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-fg">System skills</h1>
          <p className="mt-1 text-xs text-fg-muted">
            Published to every space, read-only. An edit here reaches every team&rsquo;s agents on their next session.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={importInput}
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void handleImport(file);
            }}
          />
          <Button onClick={() => importInput.current?.click()} iconLeft={<UploadSimpleIcon size={16} />}>
            Import zip
          </Button>
          <Button variant="primary" onClick={() => setCreating(true)} iconLeft={<PlusIcon size={16} weight="bold" />}>
            New system skill
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell className="w-24">Status</TableHeaderCell>
              <TableHeaderCell className="w-40">Updated</TableHeaderCell>
              <TableHeaderCell className="w-12" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && skills.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-fg-tertiary">
                  <span className="inline-flex items-center gap-2">
                    <Spinner size={4} /> Loading…
                  </span>
                </TableCell>
              </TableRow>
            ) : skills.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-fg-tertiary">
                  No system skills published yet.
                </TableCell>
              </TableRow>
            ) : (
              skills.map((skill) => (
                <TableRow key={skill.id}>
                  <TableCell>
                    <Link to={`/admin/skills/${skill.id}`} className="flex min-w-0 items-center gap-2.5">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-3 text-fg-secondary">
                        <GraduationCapIcon size={16} />
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-semibold text-fg">{skill.name}</span>
                        <span className="truncate text-xs text-fg-tertiary">{skill.description}</span>
                      </span>
                    </Link>
                  </TableCell>
                  <TableCell className="text-fg-secondary">{skill.enabled ? 'Published' : 'Draft'}</TableCell>
                  <TableCell className="font-medium text-fg-secondary">{formatTimeAgo(skill.updated_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <IconButton
                        size={8}
                        aria-label={`Delete ${skill.name}`}
                        onClick={() => handleDelete(skill.id, skill.name)}
                      >
                        <TrashIcon size={16} />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {creating && (
        <NewSystemSkillModal
          existingNames={skills.map((s) => s.name)}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            await mutate();
          }}
        />
      )}
    </div>
  );
}

function NewSystemSkillModal({
  existingNames,
  onClose,
  onCreated,
}: {
  existingNames: string[];
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const nameError =
    trimmed && !isValidSkillName(trimmed)
      ? 'Lowercase letters, digits, and single hyphens only.'
      : existingNames.includes(trimmed)
        ? 'A system skill with this name already exists.'
        : null;
  const canSave = Boolean(trimmed) && !nameError && Boolean(description.trim()) && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      await createAdminSkill({ skill_md: seedSkillMd(trimmed, description.trim()) });
      await onCreated();
      toast.success(`Created "${trimmed}"`);
      onClose();
    } catch (e) {
      toast.error(errorText(e, 'Could not create this skill'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="New system skill"
      description="Published to every space, read-only."
      maxWidth="max-w-xl"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
        className="flex flex-col gap-4 px-6 pb-6"
      >
        <div className="flex flex-col gap-1.5">
          <label htmlFor="admin-skill-name" className="text-xs font-medium text-fg">
            Name
          </label>
          <Input
            id="admin-skill-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="open-a-pr"
            autoFocus
          />
          {nameError ? (
            <span className="text-2xs leading-3.5 text-danger">{nameError}</span>
          ) : (
            <span className="text-2xs leading-3.5 text-fg-tertiary">How the agent refers to this skill.</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="admin-skill-description" className="text-xs font-medium text-fg">
            When should the agent use this?
          </label>
          <Textarea
            id="admin-skill-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-24 text-xs leading-5"
            placeholder="Opens a pull request the way this repo expects. Use when asked to ship, open a PR, or push a branch."
          />
          <span className="text-2xs leading-3.5 text-fg-tertiary">
            In every request for every team that has this on — a sentence or two.
          </span>
        </div>

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" size={9} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size={9} disabled={!canSave} isLoading={saving}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
